import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useAddressLookup, type ConfidenceSource } from '@/hooks/useAddressLookup';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Search, MapPin, Loader2 } from 'lucide-react';
import type { Feature, Geometry, Polygon } from 'geojson';
import { useTranslation } from '@/hooks/useTranslation';
import { captureException } from '@/lib/sentry';
import type { AzureAddress } from '@/lib/types/azure';
import {
  extendBoundsWithGeometry,
  normalizeCorners,
  pickerDrawStyles,
  ringIsSimple,
} from '@/components/ui/mapbox-shapes';

// Set Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

const MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
} as const;
type MapStyle = keyof typeof MAP_STYLES;
type DrawMode = 'pin' | 'area';

/** The picker's red: the pin and the area. */
const PICK_COLOR = '#ef4444';

export interface LocationData {
  address: AzureAddress;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  /** Absent for a hand-placed pin — there is no geocode match to be confident about. */
  confidence?: number;
  /** `rank` for a picked suggestion: `saveLocation` re-scores it before storing. */
  confidenceSource?: ConfidenceSource;
  placeId?: string;
  /**
   * Drawn geometry (GeoJSON) — only with `allowArea`: a `Point` for a pin
   * dropped on the map, a `Polygon` for a drawn area. Written to
   * `gis_locations.geom`. Absent for a search pick (a plain address row).
   */
  geom?: Geometry;
}

interface MapboxLocationPickerProps {
  onLocationSelect: (location: LocationData) => void;
  initialLocation?: LocationData;
  className?: string;
  height?: string;
  /**
   * Offer a Pin | Area toggle and a Streets | Satellite toggle; map picks then
   * carry `geom`. Off = the original pin-only picker, unchanged.
   */
  allowArea?: boolean;
  /** Where to open when there is no `initialLocation` (e.g. a sub-space starts at its parent). */
  initialCenter?: { latitude: number; longitude: number };
  /**
   * The current pick was discarded (Clear, an invalid outline, or switching to
   * a mode with nothing picked) — a parent holding the last
   * `onLocationSelect` value must drop it.
   */
  onClear?: () => void;
}

/** The coordinates as a label — until (or instead of) a reverse-geocoded address. */
const coordinateLabel = (lat: number, lng: number) =>
  ({ formattedAddress: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }) as AzureAddress;

/**
 * Pick a point or draw an area on a Mapbox map.
 *
 * Area mode (`allowArea`) is @mapbox/mapbox-gl-draw, attached only while in
 * Area mode (it swallows touch taps and box-zoom, which Pin mode needs): click
 * corners, finish on the first/last corner or Enter; then drag a corner, click
 * a midpoint to add one, select a corner and press Delete / Remove corner.
 * One area at a time. docs/LOCATIONS.md "MapboxLocationPicker".
 */
export function MapboxLocationPicker({
  onLocationSelect,
  initialLocation,
  className = '',
  height = '400px',
  allowArea = false,
  initialCenter,
  onClear,
}: MapboxLocationPickerProps) {
  const { t } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);

  // Only a single Polygon round-trips into the area editor (its outer ring —
  // holes from a future import are dropped on the first edit). Other saved
  // types open in pin mode; a redraw replaces them.
  const initialPolygon: Polygon | null =
    initialLocation?.geom?.type === 'Polygon' ? initialLocation.geom : null;
  const initialMode: DrawMode = allowArea && initialPolygon ? 'area' : 'pin';

  // Map handlers are bound once at mount, so everything they read lives in refs.
  const modeRef = useRef<DrawMode>(initialMode);
  const drawRef = useRef<MapboxDraw | null>(null);
  // Pin and area are kept SEPARATELY, so switching Pin ↔ Area only hides the
  // inactive one and switching back restores it.
  const pinPosRef = useRef<[number, number] | null>(
    initialLocation && !initialPolygon
      ? [initialLocation.coordinates.longitude, initialLocation.coordinates.latitude]
      : null,
  );
  const areaRef = useRef<Polygon | null>(initialPolygon);
  // The last pick made in each mode this session — re-emitted on switching
  // back, and the identity an in-flight address lookup checks before it
  // upgrades a pick (a newer pick, a discard or unmount drops the upgrade).
  const lastPinPickRef = useRef<LocationData | null>(null);
  const lastAreaPickRef = useRef<LocationData | null>(null);
  // The saved location, shown in the info card in its own mode while it is
  // untouched — never re-emitted (nothing new to confirm).
  const savedPickRef = useRef<LocationData | null>(initialLocation ?? null);

  const [mode, setModeState] = useState<DrawMode>(initialMode);
  const [mapStyle, setMapStyle] = useState<MapStyle>('streets');
  const [hasArea, setHasArea] = useState(initialPolygon != null);
  // A finished outline the DB would refuse (crossing, touching, no area): not
  // emitted; the hint asks the user to fix it.
  const [areaInvalid, setAreaInvalid] = useState(false);
  const [cornerSelected, setCornerSelectedState] = useState(false);
  // Mirrored in a ref for the mount-bound keydown handler.
  const cornerSelectedRef = useRef(false);
  const setCornerSelected = (selected: boolean) => {
    cornerSelectedRef.current = selected;
    setCornerSelectedState(selected);
  };

  const [searchQuery, setSearchQueryLocal] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(initialLocation || null);
  const [isMapReady, setIsMapReady] = useState(false);

  // One fuzzy typeahead call per pause, addresses and points of interest
  // ranked together by Azure with the org-country bias — see
  // `azureMapsProxy.suggestAddresses`. (This used to fire a POI search and a
  // final-answer geocode in parallel per keystroke, then interleave them by
  // score, which is meaningless across the two endpoints.)
  const {
    reverseGeocode,
    formatAddress,
    searchResults,
    isSearching,
    setSearchQuery: setSuggestionQuery,
  } = useAddressLookup({ includePoi: true, suggestionLimit: 8 });

  const setSearchQuery = (value: string) => {
    setSearchQueryLocal(value);
    setSuggestionQuery(value);
  };

  const [showSuggestions, setShowSuggestions] = useState(false);
  // Results land asynchronously; only open the list if the input still has
  // focus, or a user who tabbed away mid-fetch gets a dropdown on nothing.
  const inputFocused = useRef(false);
  useEffect(() => {
    if (inputFocused.current && searchResults.length > 0 && searchQuery.trim().length >= 3) {
      setShowSuggestions(true);
    }
  }, [searchResults, searchQuery]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    if (!mapboxgl.accessToken) {
      console.error('Mapbox access token is not set. Please add VITE_MAPBOX_ACCESS_TOKEN to your environment variables.');
      return;
    }

    const start = initialLocation?.coordinates ?? initialCenter;
    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAP_STYLES.streets,
      center: start
        ? [start.longitude, start.latitude]
        : [4.9041, 52.3676], // Default to Amsterdam
      zoom: start ? 15 : 10,
    });
    map.current = m;

    m.addControl(new mapboxgl.NavigationControl(), 'top-right');

    m.on('load', () => {
      setIsMapReady(true);
      if (modeRef.current === 'pin' && pinPosRef.current) {
        addMarker(pinPosRef.current[0], pinPosRef.current[1]);
      }
      if (initialLocation?.geom && initialLocation.geom.type !== 'Point') {
        m.fitBounds(
          extendBoundsWithGeometry(new mapboxgl.LngLatBounds(), initialLocation.geom),
          { padding: 40, maxZoom: 18, duration: 0 },
        );
      }
    });

    m.on('click', handleMapClick);

    // Draw's events (fired on the map while it is attached).
    m.on('draw.create', handleShape);
    m.on('draw.update', handleShape);
    m.on('draw.delete', handleAreaDeleted);
    m.on('draw.modechange', keepEditing);
    // "Is there a corner trash() would remove?" — Draw's own answer, from its
    // mode state. Not selectionchange/getSelectedPoints: a corner added by
    // clicking a midpoint is drawn (and trashed) as selected but never enters
    // the store's selection. Gated on direct_select: draw_polygon reports
    // trash too (it would discard the unfinished outline).
    m.on('draw.actionable', (e: { actions: { trash: boolean } }) =>
      setCornerSelected(drawRef.current?.getMode() === 'direct_select' && e.actions.trash),
    );
    // Delete / Backspace remove the selected corner(s). Not Draw's own
    // binding: that needs its trash control, which would also add Draw's icon
    // button and make Backspace throw away an unfinished outline.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (modeRef.current !== 'area' || drawRef.current?.getMode() !== 'direct_select') return;
      // Only with a corner selected: Draw's trash() with nothing selected still
      // re-sends the unchanged shape (a pointless re-emit + paid address
      // lookup). And only from the map itself, not e.g. a zoom button.
      if (!cornerSelectedRef.current || !(e.target instanceof Element) || !e.target.closest('.mapboxgl-canvas-container, .mapboxgl-canvas')) return;
      e.preventDefault();
      drawRef.current.trash();
    };
    m.getContainer().addEventListener('keydown', onKeyDown);

    if (modeRef.current === 'area') attachDraw();

    return () => {
      // Drops any address lookup still in flight (they check these refs).
      lastPinPickRef.current = null;
      lastAreaPickRef.current = null;
      m.getContainer().removeEventListener('keydown', onKeyDown);
      m.remove(); // also runs Draw's onRemove
      map.current = null;
      drawRef.current = null;
    };
    // Mount-once: mode, shapes and the callbacks are read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add marker to map
  const addMarker = (lng: number, lat: number) => {
    if (!map.current) return;

    // Remove existing marker
    if (marker.current) {
      marker.current.remove();
    }

    // Add new marker
    pinPosRef.current = [lng, lat];
    marker.current = new mapboxgl.Marker({ color: PICK_COLOR })
      .setLngLat([lng, lat])
      .addTo(map.current);
  };

  /**
   * Reverse geocode a hand-picked spot. Somewhere with no address (open
   * desert, sea) resolves `null`; a failed lookup is reported and also falls
   * back — the coordinates stay the label (`formatted_address` is NOT NULL).
   */
  const addressAt = async (lat: number, lng: number): Promise<AzureAddress> => {
    try {
      const address = await reverseGeocode({ lat, lon: lng });
      return address?.formattedAddress ? address : coordinateLabel(lat, lng);
    } catch (error) {
      captureException(error, { feature: 'map-picker', action: 'reverse-geocode' });
      return coordinateLabel(lat, lng);
    }
  };

  // The map click handler is bound once at mount; read the callback through a
  // ref so a parent re-render's new closure is the one called.
  const onSelectRef = useRef(onLocationSelect);
  onSelectRef.current = onLocationSelect;
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  /** Drop the current pick everywhere it is held (here and in the parent). */
  const discardPick = () => {
    setSelectedLocation(null);
    onClearRef.current?.();
  };

  const emit = (locationData: LocationData) => {
    if (locationData.geom?.type === 'Polygon') lastAreaPickRef.current = locationData;
    else lastPinPickRef.current = locationData;
    setSelectedLocation(locationData);
    onSelectRef.current(locationData);
  };

  /**
   * Emit a hand-made pick at once, labelled with its coordinates, then swap in
   * the reverse-geocoded address when it arrives — unless the pick has been
   * replaced or dropped meanwhile. Confirming before the address arrives saves
   * the coordinates as the label (the same as the no-address fallback).
   */
  const emitWithAddressLater = (pick: LocationData) => {
    const isArea = pick.geom?.type === 'Polygon';
    const lastRef = isArea ? lastAreaPickRef : lastPinPickRef;
    emit(pick);
    void addressAt(pick.coordinates.latitude, pick.coordinates.longitude).then((address) => {
      if (lastRef.current !== pick) return;
      const upgraded = { ...pick, address };
      if (modeRef.current === (isArea ? 'area' : 'pin')) emit(upgraded);
      else lastRef.current = upgraded; // shown when the user switches back
    });
  };

  /** On returning to a mode: re-emit its pick, else show the untouched saved location, else nothing. */
  const restorePick = (pick: LocationData | null) => {
    if (pick) {
      emit(pick);
      return;
    }
    setSelectedLocation(modeRef.current === initialMode ? savedPickRef.current : null);
    onClearRef.current?.();
  };

  // Handle map clicks for location selection (pin mode; Draw owns area clicks)
  const handleMapClick = async (e: mapboxgl.MapMouseEvent) => {
    if (modeRef.current === 'area') return;
    const { lng, lat } = e.lngLat;
    addMarker(lng, lat);

    if (!allowArea) {
      // The original picker: one emit, with the address. A hand-placed pin
      // has no geocode match, so no confidence figure.
      emit({ address: await addressAt(lat, lng), coordinates: { latitude: lat, longitude: lng } });
      return;
    }
    if (initialMode === 'pin') savedPickRef.current = null;
    emitWithAddressLater({
      address: coordinateLabel(lat, lng),
      coordinates: { latitude: lat, longitude: lng },
      geom: { type: 'Point', coordinates: [lng, lat] },
    });
  };

  // ---- Area mode (Draw) ----------------------------------------------------

  /** Attach Draw and put the area (if any) under edit, else start drawing. */
  const attachDraw = () => {
    const m = map.current;
    if (!m || drawRef.current) return;
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      boxSelect: false,
      styles: pickerDrawStyles(PICK_COLOR),
    });
    m.addControl(draw);
    drawRef.current = draw;
    if (areaRef.current) {
      const [id] = draw.add({ type: 'Feature', properties: {}, geometry: areaRef.current });
      draw.changeMode('direct_select', { featureId: id });
    } else {
      draw.changeMode('draw_polygon');
    }
  };

  /** Detach Draw. A finished area stays in `areaRef`; an unfinished outline is dropped. */
  const detachDraw = () => {
    const draw = drawRef.current;
    if (!draw || !map.current) return;
    // Delete before leaving the mode: otherwise leaving draw_polygon with 3+
    // corners fires a draw.create the user never asked for.
    draw.deleteAll();
    map.current.removeControl(draw);
    // Draw snapshots the map's interaction settings when it connects and puts
    // them back on removal; attached before the map finished loading, its own
    // draw mode has already switched double-click zoom off by then, so the
    // "restore" would leave it off in Pin mode. The picker always wants it on
    // outside Area mode.
    map.current.doubleClickZoom.enable();
    drawRef.current = null;
    setCornerSelected(false);
  };

  /**
   * Area mode never rests in simple_select: a finished shape goes straight to
   * corner editing, and with no shape (deleted down to < 3 corners) back to
   * drawing. Draw allows redirecting from inside modechange, and API calls are
   * silent, so this cannot loop. (Never call Draw from draw.create — it fires
   * inside the mode change and would recurse.)
   */
  const keepEditing = (e: { mode: string }) => {
    const draw = drawRef.current;
    if (!draw || modeRef.current !== 'area' || e.mode !== 'simple_select') return;
    const feature = draw.getAll().features[0];
    if (feature?.id != null) draw.changeMode('direct_select', { featureId: String(feature.id) });
    else draw.changeMode('draw_polygon');
  };

  /** draw.create / draw.update: validate the outline, then emit it. */
  const handleShape = (e: { features: Feature[] }) => {
    const geometry = e.features[0]?.geometry;
    if (geometry?.type !== 'Polygon') return;
    const corners = normalizeCorners((geometry.coordinates[0] ?? []).slice(0, -1));
    if (corners.length < 3) return; // being deleted — draw.delete follows
    const area: Polygon = { type: 'Polygon', coordinates: [[...corners, corners[0]]] };
    areaRef.current = area;
    setHasArea(true);
    if (initialMode === 'area') savedPickRef.current = null;
    if (!ringIsSimple(corners)) {
      setAreaInvalid(true);
      lastAreaPickRef.current = null;
      discardPick();
      return;
    }
    setAreaInvalid(false);
    const center = extendBoundsWithGeometry(new mapboxgl.LngLatBounds(), area).getCenter();
    emitWithAddressLater({
      address: coordinateLabel(center.lat, center.lng),
      coordinates: { latitude: center.lat, longitude: center.lng },
      geom: area,
    });
  };

  const forgetArea = () => {
    areaRef.current = null;
    setHasArea(false);
    setAreaInvalid(false);
    lastAreaPickRef.current = null;
    if (initialMode === 'area') savedPickRef.current = null;
    discardPick();
  };

  /** The user removed corners until fewer than three were left. */
  const handleAreaDeleted = () => forgetArea();

  const clearArea = () => {
    const draw = drawRef.current;
    if (draw) {
      draw.deleteAll();
      draw.changeMode('draw_polygon');
    }
    setCornerSelected(false);
    forgetArea();
  };

  const removeCorner = () => {
    if (cornerSelectedRef.current && drawRef.current?.getMode() === 'direct_select') drawRef.current.trash();
  };

  const setMode = (next: DrawMode) => {
    if (next === modeRef.current) return;
    modeRef.current = next;
    setModeState(next);
    // One thing on the map at a time — the other is hidden, not lost.
    if (next === 'area') {
      marker.current?.remove();
      marker.current = null;
      attachDraw();
      restorePick(lastAreaPickRef.current);
    } else {
      detachDraw();
      if (pinPosRef.current) addMarker(pinPosRef.current[0], pinPosRef.current[1]);
      restorePick(lastPinPickRef.current);
    }
  };

  const changeStyle = (next: MapStyle) => {
    if (!next || next === mapStyle) return;
    setMapStyle(next);
    // A full rebuild, not a diff: Draw re-adds its layers on the new style, and
    // a diff would remove them one by one underneath it.
    // (mapbox-gl's typings mark the font options required; setStyle fills
    // them from the map's own settings — mapbox-gl-dev.js `setStyle`.)
    map.current?.setStyle(MAP_STYLES[next], { diff: false } as Parameters<mapboxgl.Map['setStyle']>[1]);
  };

  type Suggestion = (typeof searchResults)[number];

  // Handle selecting a search result
  const handleResultSelect = (result: Suggestion) => {
    if (!map.current) return;

    const { latitude: lat, longitude: lon } = result.coordinates;

    // Fly to the location
    map.current.flyTo({
      center: [lon, lat],
      zoom: 15,
      duration: 1000,
    });

    // In area mode a search only moves the map — the user draws next.
    if (modeRef.current === 'area') {
      setShowSuggestions(false);
      return;
    }

    // Add marker
    addMarker(lon, lat);

    // Update selected location
    const locationData: LocationData = {
      address: result.address,
      coordinates: { latitude: lat, longitude: lon },
      confidence: result.confidence,
      confidenceSource: result.confidenceSource,
      placeId: result.placeId,
    };

    emit(locationData);
    setShowSuggestions(false);
  };

  // Enter / search button: take the top suggestion. If none has arrived
  // yet the debounced query is already in flight; nothing to force.
  const handleDirectSearch = () => {
    if (!searchQuery.trim()) return;
    setShowSuggestions(false);
    if (searchResults.length > 0) {
      handleResultSelect(searchResults[0]);
    }
  };

  // Handle search input key press
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDirectSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Handle input focus/blur
  const handleInputFocus = () => {
    inputFocused.current = true;
    if (searchResults.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    inputFocused.current = false;
    // Delay hiding suggestions to allow clicks
    setTimeout(() => setShowSuggestions(false), 150);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('mapPicker.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className="pl-10"
          />

          {/* Search Suggestions Dropdown */}
          {showSuggestions && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
              {searchResults.map((result, index) => (
                <div
                  key={result.placeId ?? index}
                  className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-b-0 flex items-start gap-3"
                  onClick={() => handleResultSelect(result)}
                >
                  <div className="mt-1">
                    {result.poi ? (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    ) : (
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {result.poi?.name || formatAddress(result.address)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Badge variant="outline" className="text-xs">
                        {result.poi?.categories?.[0] || (result.poi ? t('mapPicker.resultBusiness') : t('mapPicker.resultAddress'))}
                      </Badge>
                    </div>
                    {result.poi && (
                      <div className="text-xs text-muted-foreground mt-1 truncate">
                        {formatAddress(result.address)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button
          onClick={handleDirectSearch}
          disabled={!searchQuery.trim() || isSearching}
          size="icon"
        >
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>

      {allowArea && (
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={mode}
            onValueChange={(v) => v && setMode(v as DrawMode)}
            aria-label={t('mapPicker.modeLabel')}
          >
            <ToggleGroupItem value="pin">{t('mapPicker.modePin')}</ToggleGroupItem>
            <ToggleGroupItem value="area">{t('mapPicker.modeArea')}</ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={mapStyle}
            onValueChange={(v) => changeStyle(v as MapStyle)}
            aria-label={t('mapPicker.styleLabel')}
          >
            <ToggleGroupItem value="streets">{t('mapPicker.styleStreets')}</ToggleGroupItem>
            <ToggleGroupItem value="satellite">{t('mapPicker.styleSatellite')}</ToggleGroupItem>
          </ToggleGroup>
          {mode === 'area' && (
            <div className="flex items-center gap-2 ml-auto">
              {/* The touch path for removing a corner (Delete/Backspace on a keyboard). */}
              <Button type="button" variant="outline" size="sm" onClick={removeCorner} disabled={!cornerSelected}>
                {t('mapPicker.removeCorner')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={clearArea} disabled={!hasArea}>
                {t('mapPicker.clear')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div
        ref={mapContainer}
        className="w-full rounded-lg border"
        style={{ height }}
      />

      {/* Location Info. With allowArea its space is reserved even when empty:
          the picker sits in a centred dialog, and a box appearing on Finish
          grew the dialog and moved the map ~50px under the user's cursor. */}
      <div className={allowArea ? 'min-h-[74px]' : undefined}>
      {selectedLocation && (
        <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/50">
          <MapPin className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="space-y-2 flex-1">
            <div className="font-medium">
              {formatAddress(selectedLocation.address)}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>
                {selectedLocation.coordinates.latitude.toFixed(6)}, {selectedLocation.coordinates.longitude.toFixed(6)}
              </span>
              {selectedLocation.confidence !== undefined && selectedLocation.confidenceSource !== 'rank' && (
                <Badge variant="secondary" className="text-xs">
                  {t('mapPicker.confidence', { percent: Math.round(selectedLocation.confidence * 100) })}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Instructions (two lines reserved with allowArea — the hint changes as you draw) */}
      <div className={`text-xs text-muted-foreground${allowArea ? ' min-h-8' : ''}`}>
        {isMapReady ? (
          mode === 'area' ? (
            <p className={areaInvalid ? 'text-destructive' : undefined}>
              💡 {areaInvalid
                ? t('mapPicker.areaCrosses')
                : hasArea
                  ? t('mapPicker.areaDone')
                  : t('mapPicker.areaHint')}
            </p>
          ) : (
            <p>💡 {t('mapPicker.pinHint')}</p>
          )
        ) : (
          <p>{t('mapPicker.loading')}</p>
        )}
      </div>
    </div>
  );
}
